<template>
  <div :class="['va-tinymce-input', displayRequiredError ? 'va-tinymce-input--error' : '']">
    <div class="va-tinymce-input__label-wrapper d-flex align-center">
      <p class="va-tinymce-input__label">{{ title }}<span v-if="required" class="required__icon">*</span></p>
      <template v-if="info">
        <info-tooltip :info="info"></info-tooltip>
      </template>
    </div>
    <slot name="info"></slot>
    <TinyMceEditor ref="editor" :initial-value="content" :init="initObj" v-if="initObj.content_style !== undefined" />
    <div v-if="displayRequiredError" class="va-tinymce-input__error-message">{{ title }} is required.</div>
  </div>
</template>

<script>
  import InfoTooltip from "./InfoTooltip";
  import { sendToServer } from "../../utility/mediaObjectUploadHelper";
  import { getFromServer } from "../../utility/fetchMediaObject";
  import { insertAfter } from "../../utility/insertAfter";
  import { setCaretPosition } from "@/assets/utilities/tinymce/helpers/setCaretPosition";
  import { installTelHrefGuards } from "@/assets/utilities/tinymce/helpers/telHrefGuards";
  import {
    addDialogFullscreenToggle,
    openUrlWithoutFullscreenToggle,
  } from "@/assets/utilities/tinymce/helpers/dialogFullscreen";
  import { registerLinkContextForm } from "@/assets/utilities/tinymce/helpers/linkContextForm";
  import { installLinkDialogFields } from "@/assets/utilities/tinymce/helpers/linkDialogFields";
  import { makeFontSizeEditable } from "@/assets/utilities/tinymce/helpers/editableFontSize";
  import { dataImageSizes } from "../../constant";
  import {
    HR_ICON,
    PASTE_AS_TEXT_ICON,
    FORMATS,
    REMOVE_FORMATTING_ICON,
    VIDEO_ICON,
    LINK_TARGET_MODES,
    LETTER_SPACING_ICON,
    TEXT_STYLE_ICON,
  } from "@/assets/utilities/tinymce/constants";
  import { dynamicDateConfig, editDynamicDate } from "@/assets/utilities/tinymce/plugins/dynamicDate";
  import { dynamicLocationConfig } from "@/assets/utilities/tinymce/plugins/dynamicLocation";
  import { hrBreakLineConfig } from "@/assets/utilities/tinymce/plugins/hrBreakLine";
  import { insertVideoConfig } from "@/assets/utilities/tinymce/plugins/insertVideo";
  import { columnsConfig } from "@/assets/utilities/tinymce/plugins/columns";
  import { dynamicDomainConfig } from "@/assets/utilities/tinymce/plugins/dynamicDomain";
  import { urlReplacerDialogConfig } from "@/assets/utilities/tinymce/plugins/linkReplace";
  import { openLetterSpacingDialog } from "@/assets/utilities/tinymce/plugins/letterSpacing";
  import { openLineHeightDialog } from "@/assets/utilities/tinymce/plugins/lineHeight";
  import { registerBulletColor } from "@/assets/utilities/tinymce/plugins/listMarkerColor";
  import { registerMarkerSize } from "@/assets/utilities/tinymce/plugins/listMarkerSize";
  import { openTextStyleDialog, registerTextStyleIcons } from "@/assets/utilities/tinymce/plugins/textStyle";
  import {
    addOnClickDivs,
    dynamicShortcodeAddition,
    removeOnClickDivs,
  } from "@/assets/utilities/tinymce/shortodeAddition";
  import { serializeCheckoutForm, visualizeCheckoutForm } from "@/assets/utilities/tinymce/visualization/checkoutForm";
  import {
    serializeContactFormComponent,
    visualizeContactFormComponent,
  } from "@/assets/utilities/tinymce/visualization/contactForm";
  import { serializeDripComponents, visualizeDripComponents } from "@/assets/utilities/tinymce/visualization/dripForm";
  import {
    serializeEmailFormComponents,
    visualizeEmailFormComponents,
  } from "@/assets/utilities/tinymce/visualization/emailForm";
  import { olListCustomNumeric } from "@/assets/utilities/tinymce/visualization/olNumericList";
  import Editor from "@tinymce/tinymce-vue";

  import { editMultiStepQuizConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/multiStepQuizConfig";

  import { editMultiStepQuizV2Config } from "@/assets/utilities/tinymce/shortcodeConfigs/multiStepQuizV2Config";

  import { editMultiStepQuizV3Config } from "@/assets/utilities/tinymce/shortcodeConfigs/multiStepQuizV3Config";

  import { editMultiStepQuizV4Config } from "@/assets/utilities/tinymce/shortcodeConfigs/multiStepQuizV4Config";

  import { editContentSplitConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/contentSplitConfig";

  import { visualizeMultiStepQuiz } from "@/assets/utilities/tinymce/visualization/multiStepQuiz";

  import { visualizeMultiStepQuizV2 } from "@/assets/utilities/tinymce/visualization/multiStepQuizV2";

  import { visualizeMultiStepQuizV3 } from "@/assets/utilities/tinymce/visualization/multiStepQuizV3";

  import { visualizeMultiStepQuizV4 } from "@/assets/utilities/tinymce/visualization/multiStepQuizV4";

  import { visualizeAgeInputQuiz } from "@/assets/utilities/tinymce/visualization/ageInputQuiz";
  import { visualizeNewsletterBanner } from "@/assets/utilities/tinymce/visualization/newsletterBanner";
  import { visualizeSpinWheel } from "@/assets/utilities/tinymce/visualization/spinWheel";
  import { visualizeLiveClaimFeed } from "@/assets/utilities/tinymce/visualization/liveClaimFeed";
  import { visualizeContactUsForm } from "@/assets/utilities/tinymce/visualization/contactUsForm";

  import { visualizeTimer } from "@/assets/utilities/tinymce/visualization/timer";

  import { visualizeCounter } from "@/assets/utilities/tinymce/visualization/counter";
  import { visualizeButtonsWithTextBox } from "@/assets/utilities/tinymce/visualization/buttonsWithTextBox";

  import { editCommentsWidgetConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/commentsWidgetConfig";

  import { visualizeCommentsWidget } from "@/assets/utilities/tinymce/visualization/commentsWidget";

  import { editCustomHTMLAdditionConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/customHtmlConfig";
  import { editToastConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/toastConfig";
  import { visualizeToast } from "@/assets/utilities/tinymce/visualization/toast";

  import { editLiveChatConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatConfig";
  import { editLiveChatV2Config } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatV2Config";
  import { editMultiStepFormConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/multiStepFormConfig";
  import { visualizeLiveChat } from "@/assets/utilities/tinymce/visualization/liveChat";
  import { visualizeLiveChatV2 } from "@/assets/utilities/tinymce/visualization/liveChatV2";
  import { visualizeMultiStepForm } from "@/assets/utilities/tinymce/visualization/multistepform";

  import { editLiveChatAutoConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatAutoConfig";
  import { visualizeLiveAutoChat } from "@/assets/utilities/tinymce/visualization/liveChatAuto";

  import { editLiveChatMp4Config } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatMp4Config";
  import { visualizeLiveChatMp4 } from "@/assets/utilities/tinymce/visualization/liveChatMp4";
  import { editLiveChatMp4V2Config } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatMp4V2Config";
  import { visualizeLiveChatMp4V2 } from "@/assets/utilities/tinymce/visualization/liveChatMp4V2";

  import { editAudioFormConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/audioFormConfig";
  import { visualizeAudioForm } from "@/assets/utilities/tinymce/visualization/audioForm";

  import { editBottomBarConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/bottomBarConfig";
  import { visualizeBottomBar } from "@/assets/utilities/tinymce/visualization/bottomBar";

  import shortcodeStyle from "!raw-loader!sass-loader!@core/preset/preset/shortcodes.scss";

  import definedAbility from "@/ability";
  import { visualizeCarrierNames } from "@/assets/utilities/tinymce/visualization/carrierNames";

  import { editCountdownConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/countdownConfig";
  import { editSmallTextBoxConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/smallTextBoxConfig";
  import { editContentBoxConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/contentBoxConfig";
  import { editTextUnderlineConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/textUnderlineConfig";
  import { visualizeZipCodeShortcode } from "@/assets/utilities/tinymce/visualization/zipCode";

  import { editAgeInputQuizConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/ageInputQuizConfig";
  import { editNewsletterBannerConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/newsletterBannerConfig";
  import { editSpinWheelConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/spinWheelConfig";
  import { editLiveClaimFeedConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/liveClaimFeedConfig";

  import { editImageBannerConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/imageBannerConfig";
  import { visualizeImageBanner, serializeImageBanner } from "@/assets/utilities/tinymce/visualization/imageBanner";

  import { editSocialProofConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/socialProofConfig";
  import { editNumberedStepsConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/numberedStepsConfig";
  import { visualizeNumberedSteps } from "@/assets/utilities/tinymce/visualization/numberedSteps";
  import { editSocialProofV2Config } from "@/assets/utilities/tinymce/shortcodeConfigs/socialProofV2Config";
  import { editLiveChatCTAV2Config } from "@/assets/utilities/tinymce/shortcodeConfigs/liveChatCTAV2Config";
  import { editCustomButtonConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/customButtonConfig";
  import { visualizeSocialProof, serializeSocialProof } from "@/assets/utilities/tinymce/visualization/socialProof";
  import {
    visualizeSocialProofV2,
    serializeSocialProofV2,
  } from "@/assets/utilities/tinymce/visualization/socialProofV2";

  import {
    visualizeRingbaPhoneNumber,
    serializeRingbaPhoneNumber,
  } from "@/assets/utilities/tinymce/visualization/ringbaPhoneNumber";
  import { editRingbaPhoneNumberConfig } from "@/assets/utilities/tinymce/shortcodeConfigs/ringbaPhoneNumber";
  import { visualizeDate, serializeDate } from "@/assets/utilities/tinymce/visualization/dynamicDate";

  const TOOLBARS = {
    liveChatMessagePreset: {
      statusbar: false,
      toolbar1:
        "fontsizeselect | lineSpacing letterSpacing | forecolor | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | link | image | pasteAsText | removeFormatting",
      toolbar2: "shortcodesList | dynamicLocation | dynamicDate | dynamicDomain",
    },
    textOnly: {
      statusbar: false,
      toolbar1:
        "fontselect | fontsizeselect | lineSpacing letterSpacing | forecolor | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | emoticons charmap | pasteAsText | removeFormatting",
    },
    textOnlyWithLineHeight: {
      toolbar1:
        "fontselect | fontsizeselect | lineSpacing letterSpacing | forecolor | bold italic underline strikethrough | emoticons charmap | pasteAsText | removeFormatting",
    },
    onlyBIUS: {
      statusbar: false,
      toolbar1: "bold italic underline strikethrough | lineSpacing letterSpacing",
    },
    strippedDown: {
      toolbar1:
        "formatselect | fontselect | fontsizeselect | lineSpacing letterSpacing textStyle | forecolor | bold italic strikethrough underline | alignleft aligncenter alignright alignjustify | link media image ",
      toolbar2: "shortcodesList | dynamicLocation | dynamicDate | dynamicDomain",
    },
    default: {
      toolbar1:
        "fontselect | formatselect | fontsizeselect | lineSpacing letterSpacing textStyle | forecolor | bold italic strikethrough underline | styleselect | alignleft aligncenter alignright alignjustify | bullist numlist bulletColor markerSize outdent indent | link media image | advlist | hrBreakLine | insertVideo",
      toolbar2: "shortcodesList | replaceLinks | dynamicLocation | dynamicDate | insertColumns | dynamicDomain",
    },
  };

  TOOLBARS.additionalSections = {
    toolbar1: TOOLBARS.default.toolbar1,
    toolbar2: `${TOOLBARS.default.toolbar2} | dynamicOrder`,
  };

  export default {
    name: "VaTinyMceInput",
    components: {
      TinyMceEditor: Editor,
      InfoTooltip,
    },
    props: {
      autoResize: {
        type: Boolean,
        default: false,
      },
      /**
       * Adds attributes to root element of tinymce content; (<p> tag usually)
       */
      forcedRootBlockAttrs: {
        type: Object,
        required: false,
        default: () => {
          return {};
        },
      },
      title: {
        type: String,
        default: "",
      },
      currentContent: {
        type: String,
        required: false,
        default: "",
      },
      onlyBIUS: {
        type: Boolean,
        required: false,
        default: false,
      },
      liveChatMessagePreset: {
        type: Boolean,
        required: false,
        default: false,
      },
      textOnly: {
        type: Boolean,
        required: false,
        default: false,
      },
      textOnlyWithLineHeight: {
        type: Boolean,
        required: false,
        default: false,
      },
      strippedDown: {
        type: Boolean,
        required: false,
        default: false,
      },
      additionalSections: {
        type: Boolean,
        required: false,
        default: false,
      },
      editorHeight: {
        type: Number,
        required: false,
        default: 500,
      },
      // Info disclaimer near the label
      info: {
        type: String,
        default: "",
      },
      required: {
        type: Boolean,
        default: false,
      },
      /**
       * Since we can't access vuelidate or veelidate in custom components like these, we can't exactly
       * know when to display errors. Once this prop is set to true, input displays required error.
       */
      displayErrors: {
        type: Boolean,
        default: false,
      },
      disableImageFilters: {
        type: Boolean,
        default: false,
      },
      disableResize: {
        type: Boolean,
        default: false,
      },
      imageUploadQueue: {
        type: Array,
        default: () => [],
      },
    },
    emits: ["update"],
    data() {
      var self = this;

      return {
        ability: definedAbility(),
        content: this.currentContent,
        openWindowCount: 0,
        dynamicShortcodeAddition: dynamicShortcodeAddition,
        sizesForImageFilters: this.disableImageFilters ? [] : Object.keys(dataImageSizes),
        initObj: {
          images_upload_handler: async (blobInfo, success, failure) => {
            try {
              const array = [...this.imageUploadQueue, blobInfo.id()];
              this.$emit("update:imageUploadQueue", array);
              const image = await sendToServer(blobInfo.blob(), self.sizesForImageFilters);
              success(image["@id"]);
            } catch (e) {
              failure(e);
            } finally {
              const array = this.imageUploadQueue.filter(item => item !== blobInfo.id());
              this.$emit("update:imageUploadQueue", array);
            }
          },
          remove_script_host: false,
          plugins: [
            "visualblocks",
            "fullscreen",
            "lists",
            "advlist",
            "paste",
            "link",
            "autolink",
            "image",
            "media",
            "template",
            "code",
            "emoticons",
            "charmap",
            ...(() => {
              if (this.autoResize) return ["autoresize"];

              return [];
            })(),
          ],
          link_title: false,
          target_list: LINK_TARGET_MODES.map(({ title, value }) => ({ title, value })),
          image_caption: true,
          valid_children: "+a[div],+a[span],+a[img]",
          advlist_bullet_styles: "disc,square,circle,kannada",
          content_style: undefined,
          height: this.editorHeight, // TODO: Make it variable
          resize: !this.disableResize,
          valid_elements: "*[*]",
          images_file_types: "svg,jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp,webp",
          block_unsupported_drop: true,
          extended_valid_elements:
            "script , contact-form, drip-form[title|subtitle|button-text] , lander-comments[comments-data]",
          custom_elements: "contact-form, drip-form[title|subtitle|button-text],lander-comments[comments-data]",
          menubar: this.onlyBIUS || this.liveChatMessagePreset ? false : "edit format view insert",
          relative_urls: false,
          browser_spellcheck: true,
          convert_urls: false,
          force_br_newlines: true,
          force_p_newlines: true,
          forced_root_block: "",
          lineheight_formats: "0.8 0.9 1 1.1 1.2 1.3 1.4 1.5 1.6 1.7 1.8 1.9 2 2.2 2.4 2.6 2.8 3",
          forced_root_block_attrs: this.forcedRootBlockAttrs,
          image_title: true,
          automatic_uploads: true,
          file_picker_types: "image",
          images_dataimg_filter: function (img) {
            // Does not allow BASE64 Image upload and forces upload to the server
            return !img.hasAttribute("internal-blob");
          },
          // eslint-disable-next-line no-unused-vars
          file_picker_callback: function (cb, value, meta) {
            var input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/*");
            input.onchange = function () {
              var file = this.files[0];

              var reader = new FileReader();
              reader.onload = function () {
                var id = "blobid" + new Date().getTime();
                // eslint-disable-next-line no-undef
                var blobCache = tinymce.activeEditor.editorUpload.blobCache;
                var base64 = reader.result.split(",")[1];
                var blobInfo = blobCache.create(id, file, base64);
                blobCache.add(blobInfo);
                sendToServer(blobInfo.blob(), self.sizesForImageFilters)
                  .then(res => {
                    cb(res["@id"], {
                      title: file.name,
                    });
                  })
                  .catch(e => {
                    // TODO: Maybe some nice custom messages?
                    alert(e);
                  });
              };
              reader.readAsDataURL(file);
            };
            input.click();
          },
          menu: {
            insert: {
              title: "Insert",
              items: "image link media",
            },
          },
          style_formats: [
            { title: "p", block: "p" },
            {
              title: "div",
              block: "div",
            },
            {
              title: "pre",
              block: "pre",
            },
            {
              title: "blockquote",
              block: "blockquote",
            },
          ],
          paste_data_images: true,
          link_context_toolbar: false,
          link_assume_external_targets: true,
          toolbar_mode: "sliding",
          font_formats:
            "Poppins=poppins,sans-serif; Arial=arial,helvetica,sans-serif; Georgia=georgia,serif; Verdana=verdana,geneva,sans-serif; Tahoma=tahoma,sans-serif; Helvetica=helvetica,sans-serif; Open Sans=open sans,helvetica,sans-serif; Roboto=roboto,helvetica,sans-serif; Source Sans Pro=source sans pro,helvetica,sans-serif; Lato=lato,helvetica,sans-serif; Atkinson Hyperlegible=atkinson hyperlegible,sans-serif;",
          fontsize_formats: "8px 10px 12px 14px 16px 18px 19px 20px 22px 24px 26px 28px 30px 32px 34px 36px 40px",
          formats: FORMATS,
          setup: function (editor) {
            const telHrefGuards = installTelHrefGuards(editor);

            editor.on("OpenWindow", function () {
              self.openWindowCount++;
              addDialogFullscreenToggle(editor);
              let iframeWrapper = document.querySelectorAll(".tox-dialog__body-iframe .tox-navobj");
              if (iframeWrapper[0]) {
                let loaderWrapper = document.createElement("div");
                let loader = document.createElement("div");
                let loaderText = document.createElement("p");
                loaderText.innerHTML = "Please wait…";
                loaderWrapper.id = "iframe-loader-wrapper";
                loader.id = "iframe-loader";
                loaderText.id = "iframe-loader-text";
                loaderWrapper.appendChild(loader);
                loaderWrapper.appendChild(loaderText);
                iframeWrapper[0].appendChild(loaderWrapper);
              }
            });
            editor.on("CloseWindow", function () {
              self.openWindowCount--;
            });
            // eslint-disable-next-line no-unused-vars
            editor.on("Init", function (ed) {
              makeFontSizeEditable(editor);
              self.visualizeDynamicComponents(editor);
              self.addOnClickDivs(editor);
              telHrefGuards.onInit();
              const nonEditableElements = editor.getBody().querySelectorAll('[data-editable="false"]');
              nonEditableElements.forEach(element => {
                element.setAttribute("contenteditable", "false");
              });
            });
            editor.on("NodeChange", function (e) {
              // Prevent editing of specific elements
              const nonEditableElements = editor.getBody().querySelectorAll('[data-editable="false"]');
              nonEditableElements.forEach(element => {
                element.setAttribute("contenteditable", "false");
              });

              if (editor.selection.isCollapsed()) {
                const activeDateSpan = editor.selection.getNode()?.closest?.(".dynamic-date-shortcode");
                if (activeDateSpan) activeDateSpan.setAttribute("contenteditable", "false");
              }

              telHrefGuards.onNodeChange(e);

              // Check if new content includes image
              if (e.element.nodeName == "IMG") {
                self.replaceImages(e.element, editor);
              }
              // Check if new content includes video
              if (e.element.nodeName == "VIDEO") {
                self.replaceVideosSrc(e.element, editor);
              }

              let imgItems = e.target.contentDocument.activeElement.getElementsByTagName("img");
              for (let item of imgItems) {
                self.replaceImages(item, editor);
              }

              let videoItems = e.target.contentDocument.activeElement.getElementsByTagName("video");
              for (let item of videoItems) {
                self.replaceVideosSrc(item, editor);
              }
            });
            editor.on("change", function () {
              // https://github.com/tinymce/tinymce/issues/2584#issuecomment-559805327
              self.content = editor.getContent();
              self.emitToParent();
              editor.save();
            });
            editor.on("click", e => {
              let target = e.target;

              /**
               * Sometimes, a click on some inner element of a shortcode should trigger
               * editing of the shortcode. In that case, we have to find the shortcode wrapper
               * element and use it as a target
               */
              if (target.dataset?.triggerEdit || e.target?.closest("[data-trigger-edit]")) {
                target = e.target.closest("[class*='-shortcode']");
              }

              if (target.className === "quiz-edit") {
                editMultiStepQuizConfig(editor, target.parentNode);
              } else if (target.className === "comments-edit") {
                editCommentsWidgetConfig(editor, e);
              }

              if (target.className === "toast-shortcode") {
                editToastConfig(editor, target.parentNode);
              }

              if (target.className === "vue-age-input") {
                editAgeInputQuizConfig(editor, target.parentNode);
              }

              if (target.className === "vue-newsletter-banner") {
                editNewsletterBannerConfig(editor, target.parentNode);
              }

              if (target.className === "vue-spin-wheel") {
                editSpinWheelConfig(editor, target.parentNode);
              }

              if (target.className === "vue-live-claim-feed") {
                editLiveClaimFeedConfig(editor, target.parentNode);
              }

              if (target.className === "live-chat-shortcode") {
                editLiveChatConfig(editor, target.parentNode);
              }

              if (target.className === "live-chat-v2-shortcode") {
                editLiveChatV2Config(editor, target.parentNode);
              }

              if (target.className === "live-chat-auto-shortcode") {
                editLiveChatAutoConfig(editor, target.parentNode);
              }

              if (target.className === "live-chat-mp4-shortcode") {
                editLiveChatMp4Config(editor, target.parentNode);
              }

              if (target.className === "live-chat-mp4-v2-shortcode") {
                editLiveChatMp4V2Config(editor, target.parentNode);
              }

              if (target.className === "multi-step-form-shortcode") {
                editMultiStepFormConfig(editor, target.parentNode);
              }

              if (target.className === "audio-form-shortcode") {
                editAudioFormConfig(editor, target.parentNode);
              }

              if (target.className === "bottom-bar-shortcode") {
                editBottomBarConfig(editor, target.parentNode);
              }

              if (target.className === "ringba-phone-number-edit") {
                editRingbaPhoneNumberConfig(editor, target.parentNode);
              }

              if (target.className === "v2-quiz-edit") {
                editMultiStepQuizV2Config(editor, target.parentNode);
              }

              if (target.className === "v3-quiz-edit") {
                editMultiStepQuizV3Config(editor, target.parentNode);
              }

              if (target.className === "v4-quiz-edit") {
                editMultiStepQuizV4Config(editor, target.parentNode);
              }

              if (target.className === "content-split-edit") {
                editContentSplitConfig(editor, target.parentNode);
              }

              if (target.className === "custom-partner") {
                editCustomHTMLAdditionConfig(editor, e);
              }

              if (target.className === "zip-code-shortcode") {
                return;
              }

              if (target.className === "countdown-shortcode") {
                editCountdownConfig(editor, target.parentNode);
              }

              if (target.className === "vue-image-banner") {
                editImageBannerConfig(editor, target.parentNode);
              }

              if (target.className === "vue-social-proof") {
                editSocialProofConfig(editor, target.parentNode);
              }

              if (target.className === "vue-numbered-steps") {
                editNumberedStepsConfig(editor, target.parentNode);
              }

              if (target.className === "vue-social-proof-v2") {
                editSocialProofV2Config(editor, target.parentNode);
              }

              const liveChatCTAV2 = e.target.closest && e.target.closest(".button--livechat-cta-v2");
              if (liveChatCTAV2) {
                e.preventDefault();
                editLiveChatCTAV2Config(editor, liveChatCTAV2);
              }

              const customButton = e.target.closest && e.target.closest(".custom-button");
              const customButtonWrapper = customButton && customButton.closest(".shortcode");
              if (customButtonWrapper) {
                e.preventDefault();
                editCustomButtonConfig(editor, customButtonWrapper);
              }

              const smallTextBox = e.target.closest && e.target.closest(".small-text-box");
              const smallTextBoxWrapper = smallTextBox && smallTextBox.closest(".shortcode");
              if (smallTextBoxWrapper) {
                e.preventDefault();
                editSmallTextBoxConfig(editor, smallTextBoxWrapper);
              }

              // Only a click on the box chrome (its padding or the gap between columns)
              // opens the options dialog; clicks inside columns keep editing in place.
              const contentBoxChrome =
                e.target.classList &&
                (e.target.classList.contains("content-box") || e.target.classList.contains("content-box__columns"))
                  ? e.target.closest(".content-box")
                  : null;
              if (contentBoxChrome) {
                e.preventDefault();
                editContentBoxConfig(editor, contentBoxChrome);
              }

              const textUnderline = e.target.closest && e.target.closest(".text-underline");
              const textUnderlineWrapper = textUnderline && textUnderline.closest(".shortcode");
              if (textUnderlineWrapper) {
                e.preventDefault();
                editTextUnderlineConfig(editor, textUnderlineWrapper);
              }
            });
            editor.on("dblclick", e => {
              const target = e.target.closest && e.target.closest(".dynamic-date-shortcode");
              if (target) {
                e.preventDefault();
                editDynamicDate(editor, target);
              }
            });
            editor.on("mouseover", e => {
              const span = e.target.closest?.(".dynamic-date-shortcode");
              if (!span || e.buttons) return;
              span.setAttribute("contenteditable", "false");
            });
            editor.on("mouseout", e => {
              const span = e.target.closest?.(".dynamic-date-shortcode");
              if (span) span.removeAttribute("contenteditable");
            });
            editor.ui.registry.addButton("shortcodesList", {
              text: "Design Elements",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                openUrlWithoutFullscreenToggle(editor, dynamicShortcodeAddition);
              },
            });
            editor.ui.registry.addButton("replaceLinks", {
              text: "Replace URLs",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                editor.windowManager.open(urlReplacerDialogConfig);
              },
            });
            editor.ui.registry.addButton("dynamicLocation", {
              text: "Dynamic Location",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                editor.windowManager.open(dynamicLocationConfig);
              },
            });
            editor.ui.registry.addButton("dynamicDate", {
              text: "Dynamic Date",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                editor.windowManager.open(dynamicDateConfig);
              },
            });
            editor.ui.registry.addButton("insertColumns", {
              text: "Insert columns",
              // eslint-disable-next-line no-unused-vars
              onAction: function () {
                editor.windowManager.open(columnsConfig);
              },
            });
            editor.ui.registry.addButton("dynamicDomain", {
              text: "Dynamic Domain",
              // eslint-disable-next-line no-unused-vars
              onAction: function () {
                editor.windowManager.open(dynamicDomainConfig);
              },
            });
            editor.ui.registry.addButton("dynamicOrder", {
              text: "Dynamic Order",
              tooltip: "Dynamic Order",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                editor.execCommand("mceInsertContent", false, "{{ section.number }}");
              },
            });
            editor.ui.registry.addButton("hrBreakLine", {
              text: HR_ICON,
              tooltip: "hr Break Line",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                editor.windowManager.open(hrBreakLineConfig);
              },
            });
            if (self.ability.can("upload", "media object to inulti server")) {
              editor.ui.registry.addButton("insertVideo", {
                text: VIDEO_ICON,
                tooltip: "Insert Video",
                // eslint-disable-next-line no-unused-vars
                onAction: function (_) {
                  editor.windowManager.open(insertVideoConfig);
                  const fileInput = document.getElementById("video-file-input");
                  const fileContainer = document.getElementById("video-file-container");
                  if (fileInput) {
                    fileInput.addEventListener("change", () => {
                      const file = fileInput.files?.[0];
                      if (file) {
                        fileContainer.classList.add("dragenter");
                      } else {
                        fileContainer.removeClass("dragenter");
                      }
                    });
                  }
                },
              });
            }
            editor.ui.registry.addButton("pasteAsText", {
              text: PASTE_AS_TEXT_ICON,
              tooltip: "Paste as text",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                const buttonSelector = '.tox-tbtn[title="Paste as text"]';
                const button = document.querySelector(buttonSelector);
                if (button) {
                  button.classList.toggle("tox-tbtn--enabled");
                }
                editor.execCommand("mceTogglePlainTextPaste");
              },
            });
            editor.ui.registry.addButton("removeFormatting", {
              text: REMOVE_FORMATTING_ICON,
              tooltip: "Remove formatting",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                editor.execCommand("SelectAll");
                editor.execCommand("RemoveFormat");
                editor.selection.collapse(false);
              },
            });
            editor.ui.registry.addButton("letterSpacing", {
              text: LETTER_SPACING_ICON,
              tooltip: "Letter spacing",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                openLetterSpacingDialog(editor);
              },
            });
            // Not "lineHeight": toolbar names are case-insensitive and the silver theme
            // registers its own lineheight button after setup, overwriting ours.
            editor.ui.registry.addButton("lineSpacing", {
              icon: "line-height",
              tooltip: "Line height",
              // eslint-disable-next-line no-unused-vars
              onAction: function (_) {
                openLineHeightDialog(editor);
              },
            });
            registerBulletColor(editor);
            registerMarkerSize(editor);
            registerTextStyleIcons(editor);
            editor.ui.registry.addButton("textStyle", {
              text: TEXT_STYLE_ICON,
              tooltip: "Text style",
              onAction: function () {
                openTextStyleDialog(editor);
              },
            });
            registerLinkContextForm(editor);
            installLinkDialogFields(editor);
            editor.on("keyDown", function (e) {
              if (e.keyCode === 13) {
                if (e.shiftKey) {
                  // Might be needed for future shortcode modifications
                } else {
                  const parentShortcodeWrapper = editor.selection.getNode().closest(".shortcode");
                  const checkoutPlanShortcode = editor.selection.getNode().closest(".checkout-plan__list");

                  /**
                   * When 'enter' is clicked while inside of the shortcode,
                   * we have to append an empty paragraph AFTER the shortcode wrapper element
                   * and put the caret/cursor onto it, so that 'enter' click doesn't create a new line inside of the shortcode
                   */
                  if (parentShortcodeWrapper) {
                    insertAfter(parentShortcodeWrapper, document.createElement("p"));
                    const newParagraphElement = editor.dom.getNext(parentShortcodeWrapper, "p");
                    setCaretPosition(editor, newParagraphElement, true);
                  }

                  if (parentShortcodeWrapper !== null && checkoutPlanShortcode === null) {
                    e.preventDefault();
                  }
                }
              }
            });
            // eslint-disable-next-line no-unused-vars
            editor.on("submit", function (_) {
              self.serializeDynamicComponents(editor);
              removeOnClickDivs(editor);
            });
          },
        },
      };
    },
    computed: {
      displayRequiredError() {
        return this.required && !this.content && this.displayErrors;
      },
    },
    watch: {
      currentContent: {
        handler(newVal) {
          this.content = newVal;
        },
        immediate: true,
      },
      openWindowCount: {
        handler(newVal) {
          if (newVal === 0) {
            document.querySelector("html").classList.remove("disable-scroll");
          } else {
            document.querySelector("html").classList.add("disable-scroll");
          }
        },
        immediate: true,
      },
      "$store.state.tinymceCss": {
        handler(newVal) {
          if (newVal) {
            this.loadStyles();
          }
        },
        immediate: true,
      },
    },
    methods: {
      emitToParent() {
        this.$emit("update", this.content);
      },
      updateContent(content) {
        if (this.$refs.editor) {
          this.$refs.editor.editor.setContent(content);
        }
      },
      flushContent() {
        if (!this.$refs.editor?.editor) return;

        this.content = this.$refs.editor.editor.getContent();
        this.emitToParent();
      },
      loadStyles() {
        const data = this.$store.state.tinymceCss;
        const fontFaces = `
          @import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
        `;
        const contentStyle = `${fontFaces} ${shortcodeStyle} ${data}`;
        this.initObj.content_style = contentStyle;
        if (this.strippedDown) {
          this.initObj = {
            ...TOOLBARS.strippedDown,
            ...this.initObj,
          };
        } else if (this.onlyBIUS) {
          this.initObj = {
            ...TOOLBARS.onlyBIUS,
            ...this.initObj,
          };
        } else if (this.textOnlyWithLineHeight) {
          this.initObj = {
            ...TOOLBARS.textOnlyWithLineHeight,
            ...this.initObj,
          };
        } else if (this.textOnly) {
          this.initObj = {
            ...TOOLBARS.textOnly,
            ...this.initObj,
          };
        } else if (this.liveChatMessagePreset) {
          this.initObj = {
            ...TOOLBARS.liveChatMessagePreset,
            ...this.initObj,
          };
        } else if (this.additionalSections) {
          this.initObj = {
            ...TOOLBARS.additionalSections,
            ...this.initObj,
          };
        } else {
          this.initObj = {
            ...TOOLBARS.default,
            ...this.initObj,
          };
        }
      },
      async replaceVideosSrc(item, editor) {
        const parsedContent = this.parseToHtml(editor.getContent());
        for (const videoItem of parsedContent.querySelectorAll("video")) {
          const videoElement = videoItem;
          if (videoElement.src.split("/api/media_objects/")[1]) {
            const videoId = "/api/media_objects/" + videoElement.src.split("/api/media_objects/")[1];
            const video = await getFromServer(videoId);

            videoElement.src = process.env.VUE_APP_API + video.contentUrl;

            editor.setContent(parsedContent.body.innerHTML);
            editor.save();
          }
          if (videoElement.poster.split("/api/media_objects/")[1]) {
            const videoId = "/api/media_objects/" + videoElement.poster.split("/api/media_objects/")[1];
            const video = await getFromServer(videoId);

            videoElement.poster = process.env.VUE_APP_API + video.contentUrl;

            editor.setContent(parsedContent.body.innerHTML);
            editor.save();
          }
        }
      },
      async replaceImages(item, editor) {
        const imageElement = item;
        // The split function removes the base url (origin) which is not needed for
        // next call
        if (imageElement.src.split("/api/media_objects/")[1]) {
          const imageId = "/api/media_objects/" + imageElement.src.split("/api/media_objects/")[1];

          try {
            const image = await getFromServer(imageId, self.sizesForImageFilters);
            if (this.ability.can("upload", "media object to inulti server")) {
              imageElement.src = process.env.VUE_APP_API + image.contentUrl;

              // tinymce property: needed for visualization
              imageElement.setAttribute("data-mce-src", process.env.VUE_APP_API + image.contentUrl);

              let imgSrcSetString = "";
              if (typeof image.sizes === "object" && !Array.isArray(image.sizes) && image.sizes !== null) {
                for (let [key] of Object.entries(image.sizes)) {
                  if (dataImageSizes[key]) {
                    imgSrcSetString += process.env.VUE_APP_API + image.sizes[key] + " " + dataImageSizes[key] + ", ";
                  }
                }

                imageElement.srcset = imgSrcSetString;
              }

              imageElement.dataset.image = "";
              editor.save();
            } else {
              imageElement.src = image.contentUrl;
              imageElement.setAttribute("data-mce-src", image.contentUrl);
              editor.save();
            }
          } catch (e) {
            console.error(e);
          }
        }
      },
      visualizeDynamicComponents(editor) {
        const parsedContent = this.parseToHtml(editor.getContent());
        visualizeDripComponents(parsedContent);
        visualizeCheckoutForm(parsedContent);

        visualizeMultiStepQuiz(parsedContent);
        visualizeMultiStepQuizV2(parsedContent);
        visualizeMultiStepQuizV3(parsedContent);
        visualizeMultiStepQuizV4(parsedContent);
        visualizeCommentsWidget(parsedContent);
        visualizeTimer(parsedContent);
        visualizeToast(parsedContent);
        visualizeLiveChat(parsedContent);
        visualizeLiveChatV2(parsedContent);
        visualizeRingbaPhoneNumber(parsedContent);
        visualizeLiveAutoChat(parsedContent);
        visualizeLiveChatMp4(parsedContent);
        visualizeLiveChatMp4V2(parsedContent);
        visualizeMultiStepForm(parsedContent);
        visualizeAudioForm(parsedContent);
        visualizeBottomBar(parsedContent);
        visualizeCounter(parsedContent);
        visualizeCarrierNames(parsedContent);
        visualizeZipCodeShortcode(parsedContent);
        visualizeButtonsWithTextBox(parsedContent);
        visualizeAgeInputQuiz(parsedContent);
        visualizeNewsletterBanner(parsedContent);
        visualizeNumberedSteps(parsedContent);
        visualizeSpinWheel(parsedContent);
        visualizeLiveClaimFeed(parsedContent);
        visualizeContactUsForm(parsedContent);
        // visualizeCommentsComponents(parsedContent) // TODO: Fix email template
        visualizeContactFormComponent(parsedContent);
        visualizeEmailFormComponents(parsedContent);
        visualizeImageBanner(parsedContent);
        visualizeSocialProof(parsedContent);
        visualizeSocialProofV2(parsedContent);
        visualizeDate(parsedContent);

        parsedContent.querySelectorAll(".columns, .content-box").forEach(element => {
          element.classList.add("dash-highlight");
        });
        // once all content is replaced, set it to editor
        editor.setContent(parsedContent.body.innerHTML);
      },
      /**
       * NONE of this works, nor has it ever worked to properly serialize and emit changed content.
       * 1) - editor.setContent doesn't trigger 'change' event of the editor, thus nothing is emitted;
       * 2) - Even if serialized content is emitted, it happens right after the form is submitted (see 'submit' event handler of the editor)
       *      meaning that unserialized data is sent to BE.
       * TODO: Cleanup;
       */
      serializeDynamicComponents(editor) {
        const parsedContent = this.parseToHtml(editor.getContent());

        serializeDripComponents(parsedContent);
        serializeCheckoutForm(parsedContent);
        serializeEmailFormComponents(parsedContent);

        // serializeCommentsComponents(parsedContent) // TODO: Fix email template
        serializeContactFormComponent(parsedContent);
        serializeRingbaPhoneNumber(parsedContent);
        serializeImageBanner(parsedContent);
        serializeSocialProof(parsedContent);
        serializeSocialProofV2(parsedContent);
        olListCustomNumeric(parsedContent);
        editor.setContent(parsedContent.body.innerHTML);
      },
      parseToHtml(content) {
        return new DOMParser().parseFromString(content, "text/html");
      },
      addOnClickDivs,
      removeOnClickDivs,
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/typography/index.scss";
  @import "../../@core/preset/preset/functions.scss";

  .va-tinymce-input {
    .va-tinymce-input__label {
      @include text-caption-bold();
      color: get-color("subtitle");
      &-wrapper {
        margin-bottom: 6px;
        .required__icon {
          color: get-color("primary");
        }
      }
    }

    &--error {
      &::v-deep {
        .tox-tinymce {
          border: 1px solid get-color("error") !important;
        }
      }
    }
    &__error-message {
      @include text-caption();
      color: get-color("error");
    }
  }

  .no-scroll {
    overflow: hidden;
  }
</style>
